import { FC, useState } from 'react';
import { Loader2, CheckCircle2, RefreshCw, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@deepguide-ai/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import { Input } from '@deepguide-ai/ui/input';
import { Label } from '@deepguide-ai/ui/label';
import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import type { ConfigModalProps, IntegrationFeature, IntegrationProvider } from '../types';
import { IntegrationIcon } from '../icons';

type MdmProviderId = 'MicrosoftIntune' | 'FleetDM' | 'JamfPro';

interface MdmProviderDefinition {
  integrationType: MdmProviderId;
  name: string;
  description: string;
  fields: FieldDefinition[];
}

interface FieldDefinition {
  key: string;
  label: string;
  placeholder: string;
  type?: 'text' | 'password' | 'url';
  required?: boolean;
  helpText?: string;
}

const PROVIDERS: Record<MdmProviderId, MdmProviderDefinition> = {
  MicrosoftIntune: {
    integrationType: 'MicrosoftIntune',
    name: 'Microsoft Intune',
    description: 'Scan managed devices for compliance state, encryption, and enrollment via Microsoft Graph API.',
    fields: [
      {
        key: 'tenantId',
        label: 'Tenant ID',
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        required: true,
        helpText: 'Azure AD tenant ID from your App Registration',
      },
      {
        key: 'clientId',
        label: 'Client ID',
        placeholder: 'Application (client) ID',
        required: true,
        helpText: 'Client ID from Azure AD App Registration',
      },
      {
        key: 'clientSecret',
        label: 'Client Secret',
        placeholder: 'Client secret value',
        type: 'password',
        required: true,
        helpText: 'Requires DeviceManagementManagedDevices.Read.All permission',
      },
    ],
  },
  FleetDM: {
    integrationType: 'FleetDM',
    name: 'FleetDM',
    description: 'Scan hosts for disk encryption, policy compliance, and MDM enrollment via Fleet REST API.',
    fields: [
      {
        key: 'serverUrl',
        label: 'Server URL',
        placeholder: 'https://fleet.yourcompany.com',
        type: 'url',
        required: true,
        helpText: 'Your FleetDM server URL (no trailing slash)',
      },
      {
        key: 'apiToken',
        label: 'API Token',
        placeholder: 'Fleet API token',
        type: 'password',
        required: true,
        helpText: 'Generate from My Account → Get API Token in Fleet UI',
      },
    ],
  },
  JamfPro: {
    integrationType: 'JamfPro',
    name: 'Jamf Pro',
    description: 'Scan managed Macs for FileVault, firewall, Gatekeeper, SIP, and configuration profiles.',
    fields: [
      {
        key: 'serverUrl',
        label: 'Jamf Pro URL',
        placeholder: 'https://yourcompany.jamfcloud.com',
        type: 'url',
        required: true,
        helpText: 'Your Jamf Pro instance URL',
      },
      {
        key: 'clientId',
        label: 'Client ID',
        placeholder: 'API Integration client ID',
        required: true,
        helpText: 'Create an API Integration in Jamf Pro Settings → API Integrations',
      },
      {
        key: 'clientSecret',
        label: 'Client Secret',
        placeholder: 'API Integration client secret',
        type: 'password',
        required: true,
        helpText: 'Requires Read access to Computers scope',
      },
    ],
  },
};

function MdmManageDialog({
  provider,
  open,
  onOpenChange,
  onComplete,
  workspaceId,
  integration,
  onReconfigure,
}: ConfigModalProps & {
  provider: MdmProviderId;
  onReconfigure: () => void;
}) {
  const definition = PROVIDERS[provider];
  const [isScanning, setIsScanning] = useState(false);

  const metadata = (integration?.metadata ?? {}) as Record<string, any>;
  const config = (integration?.config ?? {}) as Record<string, any>;
  const lastSyncAt: string | undefined = metadata.lastSyncAt;
  const totalDevices: number | undefined = metadata.totalDevices;
  const compliantDevices: number | undefined = metadata.compliantDevices;

  // Surface the server/tenant so the user can verify which account is linked
  const endpointLabel =
    provider === 'MicrosoftIntune' ? 'Tenant ID' : provider === 'FleetDM' ? 'Server URL' : 'Jamf Pro URL';
  const endpointValue =
    provider === 'MicrosoftIntune' ? config.tenantId : config.serverUrl;

  const handleSync = async () => {
    setIsScanning(true);
    try {
      const result = await trpcClient.integrations.runMdmScan.mutate({ provider });
      const { created, updated, errors } = result.syncResult;
      toast.success(
        `Synced ${created + updated} device(s)${created ? ` (${created} new)` : ''}${errors ? `, ${errors} failed` : ''}`,
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.integrations.listMdmConnections.queryKey(),
        }),
      ]);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Sync failed';
      toast.error(`Sync failed: ${msg}`);
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>{definition.name}</DialogTitle>
          <DialogDescription>
            Manage your {definition.name} integration and sync managed devices.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-4">
          <div className="rounded-sm border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Status</span>
              <span className="flex items-center gap-1.5 text-[13px] text-status-green">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Connected
              </span>
            </div>

            {endpointValue && (
              <div>
                <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">{endpointLabel}</span>
                <p className="text-[13px] text-text-body mt-0.5 break-all">{endpointValue}</p>
              </div>
            )}

            {typeof totalDevices === 'number' && (
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Devices</span>
                  <p className="text-[16px] font-semibold text-text-secondary">{totalDevices}</p>
                </div>
                <div>
                  <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Compliant</span>
                  <p className="text-[16px] font-semibold text-status-green">{compliantDevices ?? '—'}</p>
                </div>
              </div>
            )}

            {lastSyncAt && (
              <div>
                <span className="text-[12px] font-medium uppercase tracking-wider text-text-subtle">Last Sync</span>
                <p className="text-[13px] text-text-body mt-0.5">
                  {new Date(lastSyncAt).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={onReconfigure}
            disabled={isScanning}
            className="mr-auto h-8 text-[12px] text-text-body"
          >
            <Pencil className="h-3 w-3 mr-1" />
            Update credentials
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isScanning}>
            Close
          </Button>
          <Button onClick={handleSync} disabled={isScanning}>
            {isScanning ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync Devices
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MdmConfigModal({
  provider,
  open,
  onOpenChange,
  onComplete,
  workspaceId,
}: ConfigModalProps & { provider: MdmProviderId }) {
  const definition = PROVIDERS[provider];
  const [isSaving, setIsSaving] = useState(false);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(definition.fields.map((f) => [f.key, ''])),
  );

  const handleSave = async () => {
    // Validate required fields
    for (const field of definition.fields) {
      if (field.required && !values[field.key]?.trim()) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    setIsSaving(true);
    try {
      // Upsert credentials
      await trpcClient.integrations.upsertMdmConnection.mutate({
        provider,
        config: Object.fromEntries(
          Object.entries(values).map(([k, v]) => [k, v.trim()]),
        ),
      });

      // Test the connection
      const testResult = await trpcClient.integrations.testMdmConnection.mutate({
        provider,
      });

      if (!testResult.success) {
        toast.error(testResult.message || 'Connection test failed');
        return;
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.integrations.listMdmConnections.queryKey(),
        }),
      ]);

      toast.success(`${definition.name} connected`);
      onOpenChange(false);
      onComplete();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to save';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Connect {definition.name}</DialogTitle>
          <DialogDescription>{definition.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-4">
          {definition.fields.map((field) => (
            <div key={field.key} className="space-y-2">
              <Label htmlFor={`mdm-${field.key}`}>
                {field.label}
                {field.required && <span className="text-destructive"> *</span>}
              </Label>
              <Input
                id={`mdm-${field.key}`}
                type={field.type ?? 'text'}
                placeholder={field.placeholder}
                value={values[field.key] ?? ''}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                }
              />
              {field.helpText && (
                <p className="text-[11px] text-text-placeholder">{field.helpText}</p>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function createMdmProvider(provider: MdmProviderId): IntegrationProvider {
  const definition = PROVIDERS[provider];

  const IconWrapper: FC = () => <IntegrationIcon type={provider} />;

  const ConfigModalForProvider: FC<ConfigModalProps> = (props) => {
    const [forceConfigure, setForceConfigure] = useState(false);
    const isConnected = !!props.integration && !forceConfigure;

    if (isConnected) {
      return (
        <MdmManageDialog
          {...props}
          provider={provider}
          onReconfigure={() => setForceConfigure(true)}
        />
      );
    }
    return (
      <MdmConfigModal
        {...props}
        provider={provider}
        onOpenChange={(open) => {
          props.onOpenChange(open);
          if (!open) setForceConfigure(false);
        }}
      />
    );
  };

  return {
    id: definition.integrationType,
    name: definition.name,
    description: definition.description,
    Icon: IconWrapper,
    ConfigModal: ConfigModalForProvider,
    handleConnect: async ({ onSuccess }) => {
      onSuccess?.({ provider });
      return { success: true };
    },
    getFeatures: (): IntegrationFeature[] => [
      {
        id: 'mdm_scan',
        name: 'Device compliance scan',
        description: 'Check device encryption, compliance, and enrollment status',
        enabled: true,
      },
    ],
    syncData: async () => {
      return;
    },
  };
}

export const IntuneProvider = createMdmProvider('MicrosoftIntune');
export const FleetDMProvider = createMdmProvider('FleetDM');
export const JamfProProvider = createMdmProvider('JamfPro');
